import { useEffect } from "react";
import PalmHarborHeroSection from "./components/HeroSection";
import PalmHarborStickyNav from "./components/StickyNav";
import PalmHarborProcessingTimeSection from "./components/ProcessingTimeSection";
import PalmHarborServicesSection from "./components/ServicesSection";
import PalmHarborLocalAreaSection from "./components/LocalAreaSection";
import PalmHarborTravelTimingSection from "./components/TravelTimingSection";
import PalmHarborDestinationsSection from "./components/DestinationsSection";
import PalmHarborTestimonialsSection from "./components/TestimonialsSection";
import PalmHarborAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PalmHarborComparisonSection from "./components/ComparisonSection";
import PalmHarborMistakesSection from "./components/MistakesSection";
import PalmHarborHowItWorksSection from "./components/HowItWorksSection";
import PalmHarborFAQSection, { faqs } from "./components/FAQSection";
import PalmHarborHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PalmHarborFinalCTA from "./components/FinalCTA";

export default function PalmHarborPage() {
  useEffect(() => {
    document.title = "Emergency Passport Palm Harbor, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/palm-harbor/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Palm Harbor, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Pinellas County families, golfers, retirees, and Tampa Bay travelers.");
    setMeta("geo.placename", "Palm Harbor, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "28.0781;-82.7637");
    setMeta("ICBM", "28.0781, -82.7637");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Palm Harbor — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Palm Harbor and northern Pinellas County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Palm Harbor",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Palm Harbor",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PalmHarborHeroSection />
      <PalmHarborStickyNav />
      <PalmHarborProcessingTimeSection />
      <PalmHarborServicesSection />
      <PalmHarborLocalAreaSection />
      <PalmHarborTravelTimingSection />
      <PalmHarborDestinationsSection />
      <PalmHarborTestimonialsSection />
      <PalmHarborAcceptanceFacilitiesSection />
      <PalmHarborComparisonSection />
      <PalmHarborMistakesSection />
      <PalmHarborHowItWorksSection />
      <PalmHarborFAQSection />
      <PalmHarborHelpfulResourcesSection />
      <PalmHarborFinalCTA />
    </div>
  );
}